export type AuthChannelEvent =
  | { type: "logout"; tabId: string }
  | { type: "signed-out"; tabId: string }
  | { type: "login"; tabId: string }
  | { type: "tab-announce"; tabId: string }
  | { type: "tab-reply"; tabId: string; toTabId: string };

const CHANNEL_NAME = "quotrak-auth";
const STORAGE_KEY = "quotrak:auth-event";

function generateTabId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    try {
      return crypto.randomUUID();
    } catch {
      // fall through
    }
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export const TAB_ID = generateTabId();

const listeners = new Set<(event: AuthChannelEvent) => void>();
let bc: BroadcastChannel | null = null;

function dispatch(event: AuthChannelEvent | null) {
  if (!event || event.tabId === TAB_ID) return;
  for (const fn of listeners) {
    try {
      fn(event);
    } catch {
      // ignore listener errors
    }
  }
}

if (typeof window !== "undefined") {
  if (typeof BroadcastChannel !== "undefined") {
    try {
      bc = new BroadcastChannel(CHANNEL_NAME);
      bc.onmessage = (msg) => {
        dispatch(msg?.data as AuthChannelEvent);
      };
    } catch {
      bc = null;
    }
  }

  // Always listen on storage as a safety-net fallback. When BroadcastChannel
  // is supported the publish path skips localStorage so we won't get duplicate
  // events. When it isn't supported, this is the only delivery channel.
  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      const data = JSON.parse(event.newValue) as AuthChannelEvent;
      dispatch(data);
    } catch {
      // ignore malformed payloads
    }
  });
}

export function publishAuthEvent(event: AuthChannelEvent): void {
  if (bc) {
    try {
      bc.postMessage(event);
      return;
    } catch {
      // fall through to localStorage fallback
    }
  }
  if (typeof window === "undefined") return;
  try {
    // Append a timestamp so identical successive events still fire `storage`.
    const payload = JSON.stringify({ ...event, _ts: Date.now() });
    localStorage.setItem(STORAGE_KEY, payload);
    // Removing immediately keeps the key from sticking around in storage,
    // and the `storage` event has already fired in other tabs by this point.
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage may be unavailable (private mode, quota, etc.)
  }
}

export function subscribeAuthEvents(
  listener: (event: AuthChannelEvent) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
